// Copyright 2025 Sorama B.V.

import { Unit, UnitType } from "./Unit";

/**
 * A unit made up of two other units (can also be composite), for now only division
 */
export class CompositeUnit extends Unit {
    private _numerator: Unit;
    private _denominator: Unit;

    // Constructor to create a composite unit from numerator and denominator units
    public constructor(
        numerator: Unit,
        denominator: Unit,
        unitType: typeof UnitType,
        options?: {
            unitFamily?: typeof UnitType;
            aliases?: string[];
            preferredUnit?: string;
        }
    ) {
        const defaultUnit = `${numerator.unit}/${denominator.unit}`;
        const aliases = options?.aliases || [];
        super(options?.preferredUnit ?? defaultUnit, numerator.conversion / denominator.conversion, unitType, {
            unitFamily: options?.unitFamily,
            aliases,
        });

        this._numerator = numerator;
        this._denominator = denominator;

        this._unitType = unitType;

        // Creates all possible (combination) aliases of each unit's aliases
        this._aliases = [defaultUnit, ...aliases];
        for (const alias of numerator.aliases) {
            for (const denomAlias of denominator.aliases) {
                this._aliases.push(`${alias}/${denomAlias}`);
            }
        }

        if (options?.preferredUnit) {
            this._aliases.unshift(options.preferredUnit);
        }

        // Remove the duplicates from the aliases
        this._aliases = Array.from(new Set(this._aliases));
    }

    public get numerator(): Unit {
        return this._numerator;
    }

    public get denominator(): Unit {
        return this._denominator;
    }

    public withNumerator(newNumerator: Unit): CompositeUnit {
        return new CompositeUnit(newNumerator, this._denominator, this._unitType);
    }

    public withDenominator(newDenominator: Unit): CompositeUnit {
        return new CompositeUnit(this._numerator, newDenominator, this._unitType);
    }
}
